import {
  backgroundColorOptionSchema,
  intelliSightImageStyleResponseSchema,
  intelliSightLoginResponseSchema,
  intelliSightNonRegisterResponseSchema,
  intelliSightStyleProfilePageSchema,
  intelliSightRegisterResponseSchema,
  intelliSightStyleProfileSchema,
  intelliSightTokenCheckResponseSchema,
  intelliSightVoucherRedeemResponseSchema,
  intelliSightGoogleAuthResponseSchema,
  quotaSchema,
  styleCatalogResponseSchema,
  styleOptionSchema,
  urlOrDataUriSchema,
  tokenSchema,
  type Quota,
  type BackgroundColorOption,
  type StyleOption,
  type StyleCatalogResponse,
  type TokenResponse
} from './contracts/styleTransferSchemas';

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const LOCAL_PLACEHOLDER = '/images/style-placeholder.png';

function fromLocalDateTimeParts(parts: readonly number[]): string | null {
  if (parts.length < 3) {
    return null;
  }

  const [
    year,
    month = 1,
    day = 1,
    hour = 0,
    minute = 0,
    second = 0,
    nano = 0
  ] = parts;

  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    !Number.isFinite(hour) ||
    !Number.isFinite(minute) ||
    !Number.isFinite(second) ||
    !Number.isFinite(nano)
  ) {
    return null;
  }

  const milliseconds = Math.floor(Number(nano) / 1_000_000);
  const timestamp = Date.UTC(year, month - 1, day, hour, minute, second, milliseconds);
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toISOString();
}

function normalizeDateTime(
  value: string | readonly number[] | null | undefined,
  fallback?: Date
): string {
  if (!value) {
    return (fallback ?? new Date()).toISOString();
  }

  if (Array.isArray(value)) {
    const converted = fromLocalDateTimeParts(value);
    if (converted) {
      return converted;
    }
    return (fallback ?? new Date()).toISOString();
  }

  const direct = new Date(value);
  if (!Number.isNaN(direct.getTime())) {
    return direct.toISOString();
  }

  const withZ = new Date(`${value}Z`);
  if (!Number.isNaN(withZ.getTime())) {
    return withZ.toISOString();
  }

  return (fallback ?? new Date()).toISOString();
}

function determineQuotaStatus(remaining: number): Quota['status'] {
  if (remaining < 5) {
    return 'blocked';
  }
  if (remaining <= 20) {
    return 'limited';
  }
  return 'active';
}

function buildQuota(total: number, remaining: number, refreshAt?: string | null): Quota {
  return quotaSchema.parse({
    total,
    remaining,
    status: determineQuotaStatus(remaining),
    refreshAt: refreshAt ?? null
  });
}

export function adaptDeviceTokenResponse(payload: unknown): TokenResponse {
  const parsed = intelliSightNonRegisterResponseSchema.parse(payload);
  const expiresAt = normalizeDateTime(parsed.tokenExpiryTime);
  const remainingRequests = parsed.remainingRequests;
  const total = parsed.dailyRequestLimit ?? remainingRequests;
  const quota = buildQuota(total, remainingRequests, expiresAt);

  return tokenSchema.parse({
    token: parsed.token,
    tokenType: 'device',
    expiresAt,
    quota
  });
}

export function adaptUserLoginResponse(payload: unknown): TokenResponse {
  const parsed = intelliSightLoginResponseSchema.parse(payload);
  const expiresAt = new Date(Date.now() + ONE_DAY_MS);
  const balance = parsed.balance ?? 0;
  const quota = buildQuota(balance, balance, expiresAt.toISOString());

  return tokenSchema.parse({
    token: parsed.token,
    tokenType: 'user',
    expiresAt: expiresAt.toISOString(),
    quota
  });
}

export function adaptUserRegisterResponse(payload: unknown): TokenResponse {
  const parsed = intelliSightRegisterResponseSchema.parse(payload);
  if (!parsed.token) {
    throw new Error('注册成功但后端未返回访问令牌');
  }

  const expiresAt = new Date(Date.now() + ONE_DAY_MS);
  const balance = parsed.balance ?? 0;
  const quota = buildQuota(balance, balance, expiresAt.toISOString());

  return tokenSchema.parse({
    token: parsed.token,
    tokenType: 'user',
    expiresAt: expiresAt.toISOString(),
    quota
  });
}

export interface VoucherRedeemAdaptation {
  token: TokenResponse;
  email: string;
  alias: string;
  userId?: number | null;
  redeemedAt?: string | null;
  balance?: number | null;
}

export function adaptVoucherRedeemResponse(payload: unknown): VoucherRedeemAdaptation {
  const parsed = intelliSightVoucherRedeemResponseSchema.parse(payload);
  const expiresAt = new Date(Date.now() + ONE_DAY_MS);
  const balanceSource = typeof parsed.balance === 'number' ? parsed.balance : 0;
  const safeBalance = Number.isFinite(balanceSource) && balanceSource > 0 ? balanceSource : 0;
  const quota = buildQuota(safeBalance, safeBalance, expiresAt.toISOString());
  const redeemedAt = parsed.redeemedAt ? normalizeDateTime(parsed.redeemedAt) : null;

  return {
    token: tokenSchema.parse({
      token: parsed.token,
      tokenType: 'user',
      expiresAt: expiresAt.toISOString(),
      quota
    }),
    email: parsed.email,
    alias: parsed.alias,
    userId: parsed.userId ?? null,
    redeemedAt,
    balance: parsed.balance ?? null
  };
}

export interface GoogleAuthAdaptation {
  token: TokenResponse;
  email: string;
  userName: string;
  isNewUser: boolean;
}

export function adaptGoogleAuthResponse(payload: unknown): GoogleAuthAdaptation {
  const parsed = intelliSightGoogleAuthResponseSchema.parse(payload);
  const expiresAt = normalizeDateTime(
    parsed.tokenExpiryTime,
    new Date(Date.now() + ONE_DAY_MS)
  );
  const balance = parsed.balance ?? 100;
  const quota = buildQuota(balance, balance, expiresAt);

  return {
    token: tokenSchema.parse({
      token: parsed.token,
      tokenType: 'user',
      expiresAt,
      quota
    }),
    email: parsed.email,
    userName: parsed.username || parsed.email.split('@')[0],
    isNewUser: parsed.isNewUser ?? false
  };
}

function dedupeBackgroundColors(colors: BackgroundColorOption[]): BackgroundColorOption[] {
  const seen = new Set<string>();
  const unique: BackgroundColorOption[] = [];
  colors.forEach((color) => {
    if (seen.has(color.colorCode)) {
      return;
    }
    seen.add(color.colorCode);
    unique.push(color);
  });
  return unique;
}

export function adaptStyleCatalog(payload: unknown): StyleCatalogResponse {
  const pageResult = intelliSightStyleProfilePageSchema.safeParse(payload);
  const source = pageResult.success
    ? pageResult.data.items
    : intelliSightStyleProfileSchema.array().parse(payload);

  const mapped = source
    .filter((style) => style.active !== false)
    .map<StyleOption>((style) => {
      const fallbackPreview = LOCAL_PLACEHOLDER;
      const normalizedStyleCode =
        typeof style.styleCode === 'string' ? style.styleCode.trim() : '';
      const isSafeStyleCode =
        normalizedStyleCode.length > 0 &&
        !normalizedStyleCode.includes('..') &&
        !normalizedStyleCode.includes('/') &&
        !normalizedStyleCode.includes('\\');
      const staticPreview = isSafeStyleCode
        ? `/images/image-example/${normalizedStyleCode}.webp`
        : null;
      const previewValidation = staticPreview
        ? urlOrDataUriSchema.safeParse(staticPreview)
        : null;
      const previewUrl =
        previewValidation?.success === true ? previewValidation.data : fallbackPreview;
      const rawMultiplier = typeof style.costMultiplier === 'number' && style.costMultiplier > 0
        ? style.costMultiplier
        : 1;
      const updatedAtValue = style.updatedAt ? normalizeDateTime(style.updatedAt) : undefined;
      const rawBackgrounds = Array.isArray(style.backgroundColors)
        ? style.backgroundColors
        : [];
      const normalizedBackgrounds: BackgroundColorOption[] = rawBackgrounds
        .map((entry) => {
          if (!entry || typeof entry !== 'object') {
            return null;
          }
          const code =
            typeof entry.colorCode === 'string' ? entry.colorCode.trim() : '';
          const value =
            typeof entry.colorValue === 'string' ? entry.colorValue.trim() : '';
          const descriptionSource =
            typeof entry.colorDescription === 'string'
              ? entry.colorDescription
              : typeof entry.codeDescription === 'string'
                ? entry.codeDescription
                : '';
          const description = descriptionSource.trim();
          if (!code || !value) {
            return null;
          }
          return {
            colorCode: code,
            colorValue: value,
            colorDescription: description || undefined
          };
        })
        .filter((item): item is BackgroundColorOption => Boolean(item));
      const backgroundValidation = backgroundColorOptionSchema
        .array()
        .min(1)
        .safeParse(normalizedBackgrounds);
      const validatedBackgrounds = backgroundValidation.success
        ? dedupeBackgroundColors(backgroundValidation.data).slice(0, 16)
        : undefined;
      const defaultBackgroundColorCode = (() => {
        const preferred =
          typeof style.defaultBackgroundColorCode === 'string'
            ? style.defaultBackgroundColorCode.trim()
            : '';
        if (preferred && validatedBackgrounds?.some((item) => item.colorCode === preferred)) {
          return preferred;
        }
        return validatedBackgrounds?.[0]?.colorCode;
      })();

      return {
        id: style.styleCode,
        name: style.displayName,
        description: style.description ?? undefined,
        previewUrl,
        groupCode: style.groupCode ?? undefined,
        groupName: style.groupName ?? undefined,
        costMultiplier: rawMultiplier,
        updatedAt: updatedAtValue,
        backgroundColors: validatedBackgrounds,
        defaultBackgroundColorCode
      };
    });

  const items = styleOptionSchema.array().parse(mapped);
  const fallbackSize = items.length > 0 ? items.length : 1;
  const pageInfo = {
    page: pageResult.success ? pageResult.data.page ?? 0 : 0,
    size: pageResult.success ? pageResult.data.size ?? fallbackSize : fallbackSize,
    totalElements: pageResult.success ? pageResult.data.totalElements : items.length,
    totalPages: pageResult.success ? pageResult.data.totalPages : 1,
    hasNext: pageResult.success ? pageResult.data.hasNext ?? false : false,
    hasPrevious: pageResult.success ? pageResult.data.hasPrevious ?? false : false
  };

  return styleCatalogResponseSchema.parse({
    items,
    pageInfo
  });
}

export interface ImageStyleResult {
  dataUrl: string;
  mimeType: string;
  styleName?: string;
  providerName?: string | null;
  modelName?: string | null;
  message?: string | null;
  backgroundColorCode?: string | null;
  backgroundColorValue?: string | null;
}

export function adaptImageStyleResponse(payload: unknown): ImageStyleResult {
  const parsed = intelliSightImageStyleResponseSchema.parse(payload);
  const mimeType = parsed.mimeType ?? 'image/png';
  const dataUrl = parsed.base64Image.startsWith('data:')
    ? parsed.base64Image
    : `data:${mimeType};base64,${parsed.base64Image}`;

  return {
    dataUrl,
    mimeType,
    styleName: parsed.styleName,
    providerName: parsed.providerName ?? null,
    modelName: parsed.modelName ?? null,
    message: parsed.message ?? null,
    backgroundColorCode: parsed.backgroundColorCode ?? null,
    backgroundColorValue: parsed.backgroundColorValue ?? null
  };
}

export function adaptTokenCheckResponse(
  payload: unknown,
  fallbackTotal: number
): Quota {
  const parsed = intelliSightTokenCheckResponseSchema.parse(payload);
  const baselineTotal = Number.isFinite(fallbackTotal) ? fallbackTotal : 0;
  const remaining = parsed.remainingRequests ?? baselineTotal;
  const totalCandidate = Math.max(baselineTotal, remaining);
  const total = totalCandidate > 0 ? totalCandidate : remaining;
  const refreshAt = parsed.expiryTime ? normalizeDateTime(parsed.expiryTime) : null;
  return buildQuota(total, remaining, refreshAt);
}
